"use client";

/**
 * LoginForm – the shared email/password sign-in form for the light login.
 *
 * Identical fields on desktop and mobile (size is tuned per viewport via the
 * `compact` prop). Wires to the real auth: publicAuthClient.signIn.email,
 * translates errors to German, redirects to "/home" on success, supports
 * Enter-to-submit, a loading state, and an inline error banner.
 */

import type { Translate } from "@/lib/i18n/translate";
import { isPortalEntryClient } from "@/lib/portal-sign-in-path";
import { publicAuthClient } from "@/lib/public-auth-client";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  type CSSProperties,
  type FormEvent,
  type ReactElement,
  useState,
} from "react";
import {
  ACCENT,
  ACCENT_SHADOW,
  INK,
  INPUT_BG,
  INPUT_BORDER,
  translateAuthError,
} from "./login-shared";

/**
 * What the email-change confirmation link left behind in the URL.
 *
 * Better Auth redirects EVERY outcome of `/api/auth/verify-email` to this page
 * — success bare, failure with `&error=<CODE>`. The page used to drop both
 * without a word, so an expired link and a completed change looked exactly the
 * same: nothing. Whatever else is broken, the user must at least learn which
 * of the two happened.
 */
function emailChangeNotice(
  t: Translate,
  errorCode: string | null,
): { readonly tone: "info" | "error"; readonly text: string } {
  if (errorCode === null) {
    return { tone: "info", text: t("emailGeaendertJetztAnmelden") };
  }
  switch (errorCode) {
    case "TOKEN_EXPIRED":
      return { tone: "error", text: t("bestaetigungslinkAbgelaufen") };
    case "INVALID_TOKEN":
      return { tone: "error", text: t("bestaetigungslinkUngueltig") };
    case "INVALID_USER":
      return { tone: "error", text: t("bestaetigungslinkAnderesKonto") };
    case "USER_NOT_FOUND":
      return { tone: "error", text: t("bestaetigungslinkKontoWeg") };
    default:
      return { tone: "error", text: t("bestaetigungFehlgeschlagen") };
  }
}

const LABEL_STYLE: CSSProperties = {
  fontFamily:
    "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontSize: 10,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: "rgba(11,15,25,0.5)",
};

const ICON_WRAP_STYLE: CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(11,15,25,0.4)",
  display: "inline-flex",
  pointerEvents: "none",
};

function MailIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function LockIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeOffIcon(): ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

function EyeIcon(): ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ArrowRightIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function Spinner(): ReactElement {
  return (
    <svg
      className="mklogin-spinner"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1.6"
      />
      <path
        d="M8 2 a 6 6 0 0 1 6 6"
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

type LoginFormProps = {
  /** Tighter sizing for the mobile column. */
  readonly compact?: boolean;
  /** Accent color for the submit button + links (org brand color on portal). */
  readonly accent?: string;
  /** Shadow tint under the submit button, matched to `accent`. */
  readonly accentShadow?: string;
  /** Text color on the accent-filled submit button (for light brand colors). */
  readonly accentText?: string;
};

export function LoginForm({
  compact = false,
  accent = ACCENT,
  accentShadow = ACCENT_SHADOW,
  accentText = "#fff",
}: LoginFormProps): ReactElement {
  const t = useTranslations("public.loginForm");
  const tAuthError = useTranslations("public.loginShared");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Read once on mount: LoginScreen strips both params right afterwards, so a
  // live read would go blank before anyone could see it.
  const searchParams = useSearchParams();
  const [emailChangeResult] = useState<{
    readonly tone: "info" | "error";
    readonly text: string;
  } | null>(() =>
    searchParams.get("email-changed") === "1"
      ? emailChangeNotice(t, searchParams.get("error"))
      : null,
  );

  const canSubmit = email.length > 0 && password.length > 0 && !isLoading;
  const inputHeight = compact ? 48 : 50;
  const inputRadius = compact ? 10 : 11;
  const buttonHeight = compact ? 52 : 54;

  async function handleSignIn(): Promise<void> {
    setErrorMessage(null);
    setIsLoading(true);
    const result = await publicAuthClient.signIn.email({ email, password });
    setIsLoading(false);

    if (result.error) {
      const code =
        typeof result.error.code === "string" ? result.error.code : "";
      const msg = result.error.message ?? "";
      setErrorMessage(
        translateAuthError(tAuthError, code.length > 0 ? code : msg) ||
          translateAuthError(tAuthError, msg),
      );
      return;
    }

    // Staff hosts go straight to the app entry (`/home`; `/` is marketing).
    const onPortalHost = isPortalEntryClient();
    window.location.replace(onPortalHost ? "/portal-launch" : "/home");
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    await handleSignIn();
  }

  const inputBaseStyle: CSSProperties = {
    width: "100%",
    height: inputHeight,
    boxSizing: "border-box",
    border: `1px solid ${INPUT_BORDER}`,
    borderRadius: inputRadius,
    background: INPUT_BG,
    fontSize: 14.5,
    fontFamily: "inherit",
    color: INK,
    outline: "none",
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        marginTop: compact ? 24 : 28,
        display: "flex",
        flexDirection: "column",
        gap: compact ? 16 : 18,
      }}
    >
      {/* E-Mail */}
      <div>
        <label
          htmlFor="mklogin-email"
          style={{
            ...LABEL_STYLE,
            display: "block",
            marginBottom: compact ? 7 : 8,
          }}
        >
          {t("eMailAdresse")}
        </label>
        <div style={{ position: "relative" }}>
          <span style={{ ...ICON_WRAP_STYLE, left: compact ? 13 : 14 }}>
            <MailIcon />
          </span>
          <input
            id="mklogin-email"
            className="mklogin-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("nameVerwaltungDe")}
            autoComplete="username"
            disabled={isLoading}
            style={{
              ...inputBaseStyle,
              padding: `0 14px 0 ${compact ? 41 : 42}px`,
            }}
          />
        </div>
      </div>

      {/* Passwort */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: compact ? 7 : 8,
          }}
        >
          <label htmlFor="mklogin-password" style={LABEL_STYLE}>
            {t("passwort")}
          </label>
          <Link
            href="/forgot-password"
            className="mklogin-link"
            style={{
              fontSize: 12,
              color: accent,
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {t("passwortVergessen")}
          </Link>
        </div>
        <div style={{ position: "relative" }}>
          <span style={{ ...ICON_WRAP_STYLE, left: compact ? 13 : 14 }}>
            <LockIcon />
          </span>
          <input
            id="mklogin-password"
            className="mklogin-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("passwort2")}
            autoComplete="current-password"
            disabled={isLoading}
            style={{
              ...inputBaseStyle,
              padding: `0 ${compact ? 44 : 46}px 0 ${compact ? 41 : 42}px`,
            }}
          />
          <button
            type="button"
            className="mklogin-pw-toggle"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={
              showPassword ? t("passwortVerbergen") : t("passwortAnzeigen")
            }
            style={{
              position: "absolute",
              right: compact ? 6 : 7,
              top: "50%",
              transform: "translateY(-50%)",
              width: 34,
              height: 34,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "rgba(11,15,25,0.45)",
              borderRadius: 8,
              padding: 0,
              transition: "background .12s, color .12s",
            }}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </div>

      {emailChangeResult && errorMessage === null ? (
        <div
          role="status"
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border:
              emailChangeResult.tone === "error"
                ? "1px solid rgba(192,57,43,0.28)"
                : "1px solid rgba(11,15,25,0.14)",
            background:
              emailChangeResult.tone === "error"
                ? "rgba(192,57,43,0.06)"
                : "rgba(11,15,25,0.04)",
            color:
              emailChangeResult.tone === "error"
                ? "#a23b2e"
                : "rgba(11,15,25,0.72)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {emailChangeResult.text}
        </div>
      ) : null}

      {errorMessage ? (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid rgba(192,57,43,0.28)",
            background: "rgba(192,57,43,0.06)",
            color: "#a23b2e",
            fontSize: 13,
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      <button
        type="submit"
        className="mklogin-submit"
        disabled={!canSubmit}
        style={{
          marginTop: 6,
          height: buttonHeight,
          borderRadius: 12,
          border: "none",
          background: accent,
          color: accentText,
          fontFamily: "inherit",
          fontSize: 15.5,
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          boxShadow: `0 12px 26px -12px ${accentShadow}`,
        }}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t("anmelden")}
          </>
        ) : (
          <>
            {t("anmelden")}
            <ArrowRightIcon />
          </>
        )}
      </button>
    </form>
  );
}
